import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

import { useIsOfflineRecordingEnabled } from '@/app-shell/flags/desktop-flags';
import { useCachedOfflineRecordingFlagArming } from '@/app-shell/flags/offline-recording-flag-cache';
import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import type {
  DesktopConnectivity,
  OfflineConnectivityUiState,
} from '@/tauri/offline-recording/offline-recording';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

import { useNavigatorOnline } from './useNavigatorOnline';

export type { DesktopConnectivity };

/**
 * Pure classifier behind {@link useDesktopConnectivity} — the single place the
 * raw connectivity atom is turned into the resolved `online | offline | pending`
 * tri-state. Extracted so the contract is exhaustively unit-testable without a
 * React tree.
 *
 * Precedence:
 * 1. A confirmed native `online_ready_to_sync` wins outright. WKWebView keeps
 *    `navigator.onLine === true` on a link that lost its route (and can report a
 *    false offline), so a Rust-confirmed online reading must never be overridden
 *    by the browser signal.
 * 2. A confirmed unreachable atom (`offline` / `backend_unreachable`) OR a
 *    positive browser-offline signal (`navigator.onLine === false`) is
 *    `offline`. This branch is deliberately independent of the offline-recording
 *    flag — the "must not silently no-op" contract: a genuinely-offline desktop
 *    user is offline whether or not the rollout flag is on.
 * 3. `unknown` with the browser not reporting offline: a confirmed reading is
 *    only ever coming while the probe is armed (the offline-recording flag arms
 *    the Rust probe loop — see the CLAUDE.md convention). With no armed probe the
 *    atom stays `unknown` forever, so trust the browser's positive online signal
 *    and resolve to `online` rather than deferring routing/modals indefinitely.
 *    With an armed probe, resolve to `pending` and let callers wait for the real
 *    reading (Rust always emits the first transition out of `Unknown`).
 */
export function classifyDesktopConnectivity(params: {
  connectivity: OfflineConnectivityUiState;
  browserReportsOffline: boolean;
  probeArmed: boolean;
}): DesktopConnectivity {
  const { connectivity, browserReportsOffline, probeArmed } = params;

  if (connectivity === 'online_ready_to_sync') {
    return 'online';
  }
  if (
    connectivity === 'offline' ||
    connectivity === 'backend_unreachable' ||
    browserReportsOffline
  ) {
    return 'offline';
  }
  // `unknown`, browser not offline.
  return probeArmed ? 'pending' : 'online';
}

/**
 * The single source of truth for desktop connectivity across routing and
 * online-account surfaces. Returns `online` on web (this is a desktop concern;
 * web keeps its existing behaviour). On desktop it resolves the raw Rust
 * reachability atom into the {@link DesktopConnectivity} tri-state.
 *
 * Deliberately **decoupled from the offline-recording flag for offline
 * detection** (unlike `useIsOfflineConnectivity`, which no-ops entirely when the
 * flag is off): a browser-offline signal always resolves to `offline`. Only the
 * `unknown → pending` disambiguation consults whether a probe is armed, because
 * `pending` only makes sense while a confirmed reading is actually on its way.
 *
 * Split by intent:
 * - Account / onboarding nav is **pessimistic** — proceed only on `=== 'online'`
 *   and treat `pending` as "defer" (show a loader, never onboarding/banner).
 * - API-startup ("should I fetch") stays **optimistic** — keep using
 *   `useIsDesktopOnlineReady` (unknown → ready), do not migrate those onto this.
 *
 * KNOWN, PERMANENT BLIND SPOT — read this before "improving" the classifier.
 * On macOS, WKWebView pins `navigator.onLine === true` even with wifi off (see
 * `./useNavigatorOnline.ts`), so the flag-independent browser branch is inert
 * there. The Rust probe is armed exclusively by the offline-recording flag —
 * Rust deliberately sets NO boot-time target after the Jul-2026 prod LB 503
 * incident (`src-tauri/src/lib.rs`) — so with that flag off nothing arms it.
 * A genuinely offline macOS boot therefore hits neither `offline` branch and
 * this hook answers `online`. That is WRONG, and it is not fixable here: the two
 * ways to fix it are re-arming a fleet-wide boot probe (rolled back for cause)
 * or inventing a sixth heuristic (tried five times, DSK-536).
 *
 * The resolution is therefore structural and lives in the CONSUMERS: boot
 * routing no longer depends on this answer being right. `src/pages/index.tsx`
 * defaults to `/scribe` and reaches `/scribe/new-user` only on a positive
 * assertion (the cached desktop identity, which never runs through this
 * classifier), and `src/pages/scribe/index.tsx` bounds its hand-off so it cannot
 * hang when this answer is wrong. Do not add a sixth connectivity guard; make
 * the wrong answer harmless at the call site instead.
 *
 * NARROWED, NOT CLOSED. One case above is no longer blind. A machine the
 * offline-recording rollout has already touched carries a cached flag decision,
 * and recovering it arms the probe — but the fallback used to demand a
 * confirmed-offline reading, which only an armed probe can produce, so on macOS
 * (where the browser signal is pinned) nothing could enter the loop at all. That
 * loop is cut (see `isOfflineFlagFallbackState`), and `probeArmed` below now also
 * counts the recovery while it is still in flight, so such a machine resolves
 * properly instead of fabricating `online` on the first commit.
 *
 * The blind spot that survives is a machine with NO cached decision — never in
 * the rollout — offline behind a lying `navigator.onLine`. Arming for that needs
 * a probe with no flag evidence at all, i.e. the fleet-wide boot probe rolled
 * back for cause. The structural resolution above still stands and is still the
 * only correct answer for it.
 */
export function useDesktopConnectivity(): DesktopConnectivity {
  const connectivity = useAtomValue(offlineConnectivityStateAtom);
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  // A machine the rollout has touched recovers its flag from a cached decision
  // behind one local IPC read. Until that read answers, `isOfflineRecordingEnabled`
  // is `false` for reasons of TIMING, not because the probe will stay unarmed —
  // so treating it as "no reading is coming" and resolving `unknown` to a
  // confident `online` is wrong, and on macOS (where the browser signal is
  // pinned to `true`) it is the whole bug: the boot redirect fired against a
  // fabricated `online` on the first commit. `settled` is `true` immediately on
  // any machine with no cached decision, so a non-rollout boot is untouched.
  const { settled: isCachedFlagArmingSettled } = useCachedOfflineRecordingFlagArming();
  // Subscribed, not sampled: a bare `navigator.onLine` read here would go stale
  // on Windows until an unrelated re-render. Called before the non-Tauri early
  // return so the hook order is unconditional.
  const browserIsOnline = useNavigatorOnline();

  if (!isTauri()) {
    return 'online';
  }

  return classifyDesktopConnectivity({
    connectivity,
    browserReportsOffline: !browserIsOnline,
    probeArmed: isOfflineRecordingEnabled || !isCachedFlagArmingSettled,
  });
}

/**
 * Fail-safe ceiling for how long the boot loader holds on `pending` before
 * treating desktop connectivity as offline and routing to the cached app home.
 * Sits above the Rust probe's worst-case offline-detection latency (backend
 * probe + generic-internet probe), so it only ever rescues a genuinely stuck
 * probe (e.g. the residency target never resolved) — it does not race a real
 * reading, which arrives well within this window.
 */
export const DESKTOP_BOOT_CONNECTIVITY_TIMEOUT_MS = 8000;

export interface DesktopBootRoutingConnectivity {
  /** The unbounded {@link useDesktopConnectivity} reading this view derives
   * from, so a caller needing both it and the bounded gate (`/scribe`) takes one
   * read from one subscription/timer. A timed-out `pending` stays `pending`
   * here — promoting it to a confirmed `offline` is what `isRoutingOffline` is
   * for, and only that field may do it. */
  connectivity: DesktopConnectivity;
  /** Boot routing must wait — connectivity is still resolving. Hold the loader
   * and do not compute/fire the post-login redirect. */
  isPending: boolean;
  /** Resolve the boot redirect default as offline: a confirmed `offline`
   * reading, or a `pending` that hit the fail-safe timeout. */
  isRoutingOffline: boolean;
}

/**
 * Boot-routing view of {@link useDesktopConnectivity} for the two pure
 * redirect/loading routes, `src/pages/index.tsx` and `src/pages/scribe/index.tsx`.
 * Holds `pending` until the probe resolves, with a bounded fail-safe
 * ({@link DESKTOP_BOOT_CONNECTIVITY_TIMEOUT_MS}) that treats a stuck `pending` as
 * offline so the boot loader can never hang (bound every wait). The redirect
 * fires exactly once, when connectivity has resolved — which kills the
 * onboarding→loader flash caused by firing the default while still `unknown`.
 */
export function useDesktopBootRoutingConnectivity(): DesktopBootRoutingConnectivity {
  const connectivity = useDesktopConnectivity();
  const [pendingTimedOut, setPendingTimedOut] = useState(false);

  useEffect(() => {
    if (connectivity !== 'pending') {
      // Resolved (or never pending) — reset so a later pending window gets a
      // fresh timeout rather than an already-elapsed one.
      setPendingTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      // This firing is the whole reason the bound exists: the probe never
      // armed, so boot routing was RESCUED rather than resolved. Silent, it is
      // indistinguishable from a healthy resolve, and there is no way to tell
      // post-ship whether real users still reach it. No PII — the fact and the
      // ceiling are the whole payload.
      logger.warn(
        `[desktop-connectivity] Boot routing fail-safe fired after ${DESKTOP_BOOT_CONNECTIVITY_TIMEOUT_MS}ms: connectivity never resolved, routing as offline.`
      );
      setPendingTimedOut(true);
    }, DESKTOP_BOOT_CONNECTIVITY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [connectivity]);

  return {
    connectivity,
    isPending: connectivity === 'pending' && !pendingTimedOut,
    isRoutingOffline:
      connectivity === 'offline' || (connectivity === 'pending' && pendingTimedOut),
  };
}
